import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { calcEstimatedDueDate } from "features/pregnancy/utils/estimatedDueDate";
import { fromPatientCheckAnswer } from "features/pregnancy/utils/patientCheckAnswers";
import { showMutationError } from "utils/showMutationError";

const useCreatePregnancy = ({ patientId, tenantVal, onSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (values) => {
      const isLmp = !values.eddMethod || values.eddMethod === "LMP";
      const estimatedDueDate =
        !isLmp && values.estimatedDueDate
          ? values.estimatedDueDate
          : calcEstimatedDueDate(values.lastMenstrualPeriod);
      const fetusCount = values.numberOfFoetuses
        ? parseInt(values.numberOfFoetuses, 10)
        : 1;

      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(`${tenantVal}/pregnancy`, {
        tenant: tenantVal,
        patientId,
        lastMenstrualPeriod: values.lastMenstrualPeriod,
        estimatedDueDate,
        eddMethod: values.eddMethod || null,
        numberOfFoetuses: fetusCount,
        gravida: values.gravida,
        para: values.para,
        /* Yes / No / Not asked out to true / false / null — the columns are nullable booleans, so
           a check nobody was asked about stays absent instead of being sent as a No (KIN-2056). */
        hasChickenPox: fromPatientCheckAnswer(values.hasHadChickenPox),
        familyHistoryCongentialAbnormality: fromPatientCheckAnswer(
          values.noFamilyHistoryOfCongenitalAbnormality
        ),
        smoker: fromPatientCheckAnswer(values.smoker),
        confirmHcgPositive: fromPatientCheckAnswer(values.hasClinicHCGPositive),
        conductedByStaffMemberId: values.conductedByStaffMemberId
          ? parseInt(values.conductedByStaffMemberId, 10)
          : null,
        receivingFacilityCode: values.receivingFacilityCode || null,
      });

      return response;
    },
    onSuccess: (data) => {
      toast.success("Pregnancy has been added.");
      queryClient.invalidateQueries({ queryKey: ["pregnancies"] });
      onSuccess?.(data);
    },
    onError: (error) => showMutationError(error),
  });
};

export { useCreatePregnancy };
